import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { FileRejection, useDropzone } from "react-dropzone";

export const COMPANY_DOCUMENT_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
  ],
  "text/csv": [".csv"],
  "text/plain": [".txt"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};

interface DocumentDropzoneProps {
  disabled?: boolean;
  maxFiles: number;
  maxSize: number;
  onFilesAdded: (files: File[]) => void;
  onFilesRejected: (rejections: FileRejection[]) => void;
}

/** The vault's typed dropzone intentionally does not share legacy upload
 * behavior used by transactions, meetings, and directors. */
export default function DocumentDropzone({
  disabled = false,
  maxFiles,
  maxSize,
  onFilesAdded,
  onFilesRejected,
}: DocumentDropzoneProps) {
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: COMPANY_DOCUMENT_ACCEPT,
    disabled,
    maxFiles,
    maxSize,
    multiple: true,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length) onFilesAdded(acceptedFiles);
      if (rejectedFiles.length) onFilesRejected(rejectedFiles);
    },
  });

  return (
    <div
      {...getRootProps({
        className: `rounded-lg border-2 border-dashed px-5 py-7 text-center transition ${
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-gray-300 bg-gray-50 hover:border-primary hover:bg-primary/[0.02]"
        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`,
      })}
    >
      <input {...getInputProps()} />
      <ArrowUpTrayIcon className="mx-auto h-9 w-9 text-primary" />
      <p className="mt-3 text-sm font-semibold text-gray-800">
        {isDragActive ? "Drop files to add them" : "Drop files here"}
      </p>
      <p className="mt-1 text-xs text-gray-500">
        or <span className="font-medium text-primary">choose files</span> from
        your device
      </p>
      <p className="mt-2 text-xs text-gray-500">
        PDFs, office files, images, CSV, and text files are supported.
      </p>
    </div>
  );
}
